// Feedback Context Service
// Tracks user actions and captures app context for bug reports

import {
  AppContext,
  UserAction,
  ErrorLog,
  DeviceInfo,
  FeedbackReport,
  FeedbackSubmission,
  FeedbackType,
  GeneratedReport,
} from '../../types/feedback';
import { generateId } from '../../utils/idGenerator';
import { logger } from '../../utils/logger';

// ============================================================================
// CONTEXT TRACKING
// ============================================================================

const MAX_HISTORY = 20;
const MAX_ACTIONS = 50;
const MAX_ERRORS = 10;

let navigationHistory: string[] = [];
let recentActions: UserAction[] = [];
let recentErrors: ErrorLog[] = [];
let currentPage = 'DASHBOARD';
let currentComponent: string | undefined;
let currentModal: string | undefined;
let previousPage: string | undefined;
let activeQuoteId: string | undefined;
let activeCustomerId: string | undefined;
let activeInvoiceId: string | undefined;

/**
 * Set current page/view
 */
export function setCurrentPage(page: string): void {
  previousPage = currentPage;
  currentPage = page;
  navigationHistory.push(page);
  
  if (navigationHistory.length > MAX_HISTORY) {
    navigationHistory.shift();
  }
  
  logAction('NAVIGATE', page);
}

/**
 * Set current component being interacted with
 */
export function setCurrentComponent(component: string | undefined): void {
  currentComponent = component;
}

/**
 * Set current modal
 */
export function setCurrentModal(modal: string | undefined): void {
  currentModal = modal;
  if (modal) {
    logAction('OPEN_MODAL', modal);
  }
}

/**
 * Set active IDs for context
 */
export function setActiveIds(ids: {
  quoteId?: string;
  customerId?: string;
  invoiceId?: string;
}): void {
  if (ids.quoteId !== undefined) activeQuoteId = ids.quoteId;
  if (ids.customerId !== undefined) activeCustomerId = ids.customerId;
  if (ids.invoiceId !== undefined) activeInvoiceId = ids.invoiceId;
}

/**
 * Log a user action
 */
export function logAction(
  action: string,
  target?: string,
  details?: Record<string, any>
): void {
  recentActions.push({
    action,
    target,
    timestamp: new Date().toISOString(),
    details,
  });
  
  if (recentActions.length > MAX_ACTIONS) {
    recentActions.shift();
  }
}

/**
 * Log an error
 */
export function logError(error: Error, component?: string): void {
  recentErrors.push({
    message: error.message,
    stack: error.stack,
    timestamp: new Date().toISOString(),
    component,
  });
  
  if (recentErrors.length > MAX_ERRORS) {
    recentErrors.shift();
  }
}

/**
 * Get device information
 */
function getDeviceInfo(): DeviceInfo {
  const theme = document.documentElement.classList.contains('dark') ? 'dark' : 'light';
  
  return {
    userAgent: navigator.userAgent,
    screenWidth: window.screen.width,
    screenHeight: window.screen.height,
    viewportWidth: window.innerWidth,
    viewportHeight: window.innerHeight,
    platform: navigator.platform,
    isMobile: /iPhone|iPad|iPod|Android/i.test(navigator.userAgent),
    isOnline: navigator.onLine,
    theme,
  };
}

/**
 * Get current app context
 */
export function getAppContext(): AppContext {
  return {
    currentPage,
    currentComponent,
    currentModal,
    previousPage,
    navigationHistory: [...navigationHistory],
    activeQuoteId,
    activeCustomerId,
    activeInvoiceId,
    recentActions: [...recentActions].slice(-20),
    recentErrors: [...recentErrors],
    device: getDeviceInfo(),
    timestamp: new Date().toISOString(),
  };
}

/**
 * Clear context (for testing/reset)
 */
export function clearContext(): void {
  navigationHistory = [];
  recentActions = [];
  recentErrors = [];
  currentPage = 'DASHBOARD';
  currentComponent = undefined;
  currentModal = undefined;
  previousPage = undefined;
  activeQuoteId = undefined;
  activeCustomerId = undefined;
  activeInvoiceId = undefined;
}

// ============================================================================
// SCREENSHOT CAPTURE
// ============================================================================

/**
 * Capture screenshot using html2canvas (if available)
 */
export async function captureScreenshot(): Promise<string | null> {
  try {
    // Dynamic import to avoid loading if not needed
    const html2canvas = (await import('html2canvas')).default;
    
    const canvas = await html2canvas(document.body, {
      logging: false,
      useCORS: true,
      scale: 0.5, // Reduce size
    });
    
    return canvas.toDataURL('image/jpeg', 0.7);
  } catch (error) {
    logger.warn('Screenshot capture failed', error);
    return null;
  }
}

// ============================================================================
// REPORT GENERATION & CLOUD STORAGE
// ============================================================================

const LOCAL_STORAGE_KEY = 'feedback_reports';
const D1_TABLE = 'feedback_reports';

// Get storage worker URL from environment
const getStorageWorkerUrl = (): string | null => {
  // @ts-ignore - Vite env
  return typeof import.meta !== 'undefined' && import.meta.env?.VITE_STORAGE_WORKER_URL || null;
};

/**
 * Save reports to D1 (cloud storage)
 */
async function saveToCloud(reports: FeedbackReport[]): Promise<boolean> {
  const workerUrl = getStorageWorkerUrl();
  if (!workerUrl || reports.length === 0) return false;
  
  try {
    const response = await fetch(`${workerUrl}/${D1_TABLE}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include', // Include Cloudflare Access cookies
      body: JSON.stringify(reports),
    });
    return response.ok;
  } catch (error) {
    logger.warn('Failed to save feedback reports to cloud', error);
    return false;
  }
}

/**
 * Load reports from D1 (cloud storage)
 */
async function loadFromCloud(): Promise<FeedbackReport[] | null> {
  const workerUrl = getStorageWorkerUrl();
  if (!workerUrl) return null;
  
  try {
    const response = await fetch(`${workerUrl}/${D1_TABLE}`, {
      credentials: 'include', // Include Cloudflare Access cookies
    });
    if (!response.ok) return null;
    
    const data = await response.json();
    return Array.isArray(data) ? data : [];
  } catch (error) {
    logger.warn('Failed to load feedback reports from cloud', error);
    return null;
  }
}

/**
 * Create a feedback report (with cloud sync)
 */
export async function createReport(
  submission: FeedbackSubmission
): Promise<FeedbackReport> {
  const context = getAppContext();
  
  let screenshot: string | undefined;
  if (submission.includeScreenshot) {
    screenshot = (await captureScreenshot()) || undefined;
  }
  
  const report: FeedbackReport = {
    id: `feedback_${Date.now()}_${generateId()}`,
    type: submission.type,
    priority: submission.priority,
    title: submission.title,
    description: submission.description,
    steps: submission.steps,
    expectedBehavior: submission.expectedBehavior,
    actualBehavior: submission.actualBehavior,
    context,
    screenshot,
    createdAt: new Date().toISOString(),
    status: 'PENDING',
  };
  
  // Store locally first
  const localReports = getLocalReports();
  localReports.push(report);
  localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(localReports));
  
  // Sync to cloud (async, don't wait)
  saveToCloud(localReports).catch((error) => logger.warn('Failed to sync feedback reports to cloud', error));
  
  return report;
}

/**
 * Get reports from localStorage only
 */
function getLocalReports(): FeedbackReport[] {
  const stored = localStorage.getItem(LOCAL_STORAGE_KEY);
  return stored ? JSON.parse(stored) : [];
}

/**
 * Get stored reports (local first, for immediate display)
 */
export function getStoredReports(): FeedbackReport[] {
  return getLocalReports();
}

/**
 * Load and merge reports from cloud
 */
export async function syncReportsFromCloud(): Promise<FeedbackReport[]> {
  const cloudReports = await loadFromCloud();
  if (!cloudReports) return getLocalReports();
  
  const localReports = getLocalReports();
  
  // Merge: cloud takes precedence, but add any local-only reports
  const cloudIds = new Set(cloudReports.map(r => r.id));
  const localOnly = localReports.filter(r => !cloudIds.has(r.id));
  
  const merged = [...cloudReports, ...localOnly];
  
  // Sort by date (newest first)
  merged.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  
  // Update local storage
  localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(merged));
  
  // Sync merged list back to cloud if we added local-only items
  if (localOnly.length > 0) {
    saveToCloud(merged).catch((error) => logger.warn('Failed to sync merged feedback reports to cloud', error));
  }
  
  return merged;
}

/**
 * Delete a report (with cloud sync)
 */
export function deleteReport(id: string): void {
  const reports = getLocalReports().filter(r => r.id !== id);
  localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(reports));
  
  // Sync to cloud
  saveToCloud(reports).catch((error) => logger.warn('Failed to sync deleted feedback reports to cloud', error));
}

/**
 * Update report status (with cloud sync)
 */
export function updateReportStatus(
  id: string,
  status: FeedbackReport['status'],
  metadata?: {
    agentId?: string;
    agentUrl?: string;
    prUrl?: string;
    branchName?: string;
  }
): void {
  const reports = getLocalReports();
  const report = reports.find(r => r.id === id);
  if (report) {
    report.status = status;
    if (metadata) {
      if (metadata.agentId) report.agentId = metadata.agentId;
      if (metadata.agentUrl) report.agentUrl = metadata.agentUrl;
      if (metadata.prUrl) report.prUrl = metadata.prUrl;
      if (metadata.branchName) report.branchName = metadata.branchName;
    }
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(reports));
    
    // Sync to cloud
    saveToCloud(reports).catch((error) => logger.warn('Failed to sync feedback report status to cloud', error));
  }
}

// ============================================================================
// CURSOR/GITHUB REPORT GENERATION
// ============================================================================

/**
 * Map pages to likely files
 */
const PAGE_TO_FILES: Record<string, string[]> = {
  DASHBOARD: ['components/Dashboard.tsx', 'components/dashboard/*'],
  QUOTES: ['components/Dashboard.tsx', 'components/dashboard/QuoteList.tsx'],
  SCHEDULE: ['components/ScheduleCalendar.tsx', 'components/ScheduleGenerator.tsx'],
  CRM: ['components/CustomerManager.tsx'],
  ANALYTICS: ['components/AnalyticsView.tsx'],
  M365: ['components/Microsoft365Panel.tsx', 'services/outlook/*'],
  AI: ['components/ai/AIIntegrationPanel.tsx', 'services/ai/*'],
  ESTIMATOR: ['components/QuoteEstimator.tsx', 'components/estimator/*'],
  PROJECT: ['components/ProjectManager.tsx', 'components/project/*'],
  INVOICE: ['components/InvoiceManager.tsx'],
  SETTINGS: ['components/dashboard/SettingsModal.tsx'],
};

/**
 * Generate report formatted for Cursor
 */
export function generateCursorReport(report: FeedbackReport): GeneratedReport {
  const { context, type, title, description, steps, expectedBehavior, actualBehavior } = report;
  
  // Determine likely files
  const suggestedFiles = PAGE_TO_FILES[context.currentPage] || ['components/Dashboard.tsx'];
  if (context.currentModal) {
    suggestedFiles.push(`components/*${context.currentModal}*`);
  }
  if (context.currentComponent) {
    suggestedFiles.push(`components/*${context.currentComponent}*`);
  }
  
  // Generate markdown report
  const typeEmoji = {
    BUG: '🐛',
    FEATURE: '✨',
    IMPROVEMENT: '🔧',
    QUESTION: '❓',
  }[type];
  
  const priorityBadge = {
    LOW: '🟢 Low',
    MEDIUM: '🟡 Medium',
    HIGH: '🟠 High',
    CRITICAL: '🔴 Critical',
  }[report.priority];
  
  const markdown = `# ${typeEmoji} ${type}: ${title}

**Priority:** ${priorityBadge}
**Page:** ${context.currentPage}
${context.currentComponent ? `**Component:** ${context.currentComponent}` : ''}
${context.currentModal ? `**Modal:** ${context.currentModal}` : ''}
**Device:** ${context.device.isMobile ? 'Mobile' : 'Desktop'} (${context.device.viewportWidth}x${context.device.viewportHeight})
**Theme:** ${context.device.theme}

## Description
${description}

${steps ? `## Steps to Reproduce
${steps}` : ''}

${expectedBehavior ? `## Expected Behavior
${expectedBehavior}` : ''}

${actualBehavior ? `## Actual Behavior
${actualBehavior}` : ''}

## Context

### Active IDs
${context.activeQuoteId ? `- Quote: \`${context.activeQuoteId}\`` : ''}
${context.activeCustomerId ? `- Customer: \`${context.activeCustomerId}\`` : ''}
${context.activeInvoiceId ? `- Invoice: \`${context.activeInvoiceId}\`` : ''}

### Navigation History
\`\`\`
${context.navigationHistory.slice(-5).join(' → ')}
\`\`\`

### Recent Actions (last 10)
\`\`\`json
${JSON.stringify(context.recentActions.slice(-10), null, 2)}
\`\`\`

${context.recentErrors.length > 0 ? `### Recent Errors
\`\`\`json
${JSON.stringify(context.recentErrors, null, 2)}
\`\`\`` : ''}

## Suggested Files to Check
${suggestedFiles.map(f => `- \`${f}\``).join('\n')}

---
*Report ID: ${report.id}*
*Generated: ${new Date().toISOString()}*
`;

  // Generate context summary for quick reference
  const contextSummary = `Page: ${context.currentPage}${context.currentComponent ? ` > ${context.currentComponent}` : ''}${context.currentModal ? ` [Modal: ${context.currentModal}]` : ''}`;

  // Generate JSON for programmatic use
  const json = JSON.stringify({
    report: {
      id: report.id,
      type,
      priority: report.priority,
      title,
      description,
      steps,
      expectedBehavior,
      actualBehavior,
    },
    context: {
      page: context.currentPage,
      component: context.currentComponent,
      modal: context.currentModal,
      quoteId: context.activeQuoteId,
      customerId: context.activeCustomerId,
      invoiceId: context.activeInvoiceId,
      device: context.device.isMobile ? 'mobile' : 'desktop',
      theme: context.device.theme,
    },
    suggestedFiles,
    recentErrors: context.recentErrors,
  }, null, 2);

  return {
    markdown,
    json,
    contextSummary,
    suggestedFiles,
  };
}

/**
 * Copy report to clipboard
 */
export async function copyReportToClipboard(report: FeedbackReport): Promise<boolean> {
  try {
    const generated = generateCursorReport(report);
    await navigator.clipboard.writeText(generated.markdown);
    return true;
  } catch (error) {
    logger.error('Failed to copy report to clipboard', error);
    return false;
  }
}

/**
 * Download report as file
 */
export function downloadReport(report: FeedbackReport): void {
  const generated = generateCursorReport(report);
  const blob = new Blob([generated.markdown], { type: 'text/markdown' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `feedback_${report.type.toLowerCase()}_${report.id}.md`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ============================================================================
// CURSOR CLOUD AGENT API INTEGRATION
// ============================================================================

/**
 * Send feedback report to Cursor Cloud Agent API
 * 
 * This sends the report directly to Cursor via the Cloudflare Worker proxy,
 * allowing Cursor to process the feedback and potentially provide AI-assisted
 * responses or code suggestions.
 */
export async function sendToCursor(report: FeedbackReport): Promise<{
  success: boolean;
  message?: string;
  agentId?: string;
  agentUrl?: string;
  agentName?: string;
  branchName?: string;
  prUrl?: string;
  error?: string;
}> {
  try {
    // Get Cursor Worker URL from environment
    const cursorWorkerUrl = import.meta.env.VITE_CURSOR_WORKER_URL;
    
    if (!cursorWorkerUrl) {
      return {
        success: false,
        error: 'Cursor API not configured. Set VITE_CURSOR_WORKER_URL environment variable.',
      };
    }

    // Prepare payload for Cursor API
    const payload = {
      type: report.type.toLowerCase() as 'bug' | 'feature' | 'improvement' | 'question',
      priority: report.priority.toLowerCase() as 'low' | 'medium' | 'high' | 'critical',
      title: report.title,
      description: report.description,
      steps: report.steps,
      expectedBehavior: report.expectedBehavior,
      actualBehavior: report.actualBehavior,
      context: {
        page: report.context.currentPage,
        component: report.context.currentComponent,
        modal: report.context.currentModal,
        device: report.context.device.isMobile ? 'mobile' : 'desktop',
        theme: report.context.device.theme,
        recentActions: report.context.recentActions.slice(-10), // Last 10 actions
        recentErrors: report.context.recentErrors,
      },
      screenshot: report.screenshot,
      reportId: report.id,
    };

    // Send to Cursor API via Cloudflare Worker proxy
    const response = await fetch(cursorWorkerUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include', // Include Cloudflare Access cookies
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error', message: 'Unknown error' }));
      // Prioritize message field over error field for better error details
      return {
        success: false,
        error: errorData.message || errorData.error || `HTTP ${response.status}: ${response.statusText}`,
      };
    }

    const data = await response.json();
    
    // Update report status to SUBMITTED and store agent/PR URLs
    updateReportStatus(report.id, 'SUBMITTED', {
      agentId: data.agentId,
      agentUrl: data.agentUrl,
      prUrl: data.prUrl,
      branchName: data.branchName,
    });

    return {
      success: true,
      message: data.message || 'Feedback sent to Cursor successfully',
      agentId: data.agentId,
      agentUrl: data.agentUrl,
      agentName: data.agentName,
      branchName: data.branchName,
      prUrl: data.prUrl,
    };
  } catch (error) {
    logger.error('Failed to send to Cursor', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred',
    };
  }
}

// ============================================================================
// GITHUB ISSUE CREATION (URL-based)
// ============================================================================

const GITHUB_REPO = 'warrenoler/oler-quote-app';

/**
 * Generate GitHub issue URL
 */
export function getGitHubIssueUrl(report: FeedbackReport): string {
  const { type, title, description, context } = report;
  
  const labels = [type.toLowerCase()];
  if (report.priority === 'CRITICAL' || report.priority === 'HIGH') {
    labels.push('priority');
  }
  
  const body = encodeURIComponent(`
## Description
${description}

## Context
- **Page:** ${context.currentPage}
- **Device:** ${context.device.isMobile ? 'Mobile' : 'Desktop'}
- **Theme:** ${context.device.theme}

## Steps to Reproduce
${report.steps || 'N/A'}

## Expected vs Actual
- **Expected:** ${report.expectedBehavior || 'N/A'}
- **Actual:** ${report.actualBehavior || 'N/A'}

---
*Report ID: ${report.id}*
  `.trim());
  
  return `https://github.com/${GITHUB_REPO}/issues/new?title=${encodeURIComponent(`[${type}] ${title}`)}&body=${body}&labels=${labels.join(',')}`;
}

// Export service
export const feedbackContextService = {
  setCurrentPage,
  setCurrentComponent,
  setCurrentModal,
  setActiveIds,
  logAction,
  logError,
  getAppContext,
  clearContext,
  captureScreenshot,
  createReport,
  getStoredReports,
  syncReportsFromCloud,
  deleteReport,
  updateReportStatus,
  generateCursorReport,
  copyReportToClipboard,
  downloadReport,
  sendToCursor,
  getGitHubIssueUrl,
};

export default feedbackContextService;

